/**
 * Zendesk live chat / messenger integration.
 *
 * SDK loading: external (loaded via the Zendesk widget snippet, typically
 *              embedded by the host portal). No NPM dependency in tazio-fe.
 * Global:      window.zE
 * Env vars:    none
 *
 * Status: ACTIVE — primary live chat across all sites.
 *
 * Call sites (as of 2026-04-13):
 *   - src/components/atoms/customer-support-action.vue
 *       The unified `<customer-support-action>` CTA wrapper invokes
 *       `zendesk_open_chat` on click. Every chat CTA in the codebase has
 *       been migrated to this component — there are no longer any direct
 *       `window.zE` references nor `window.openZendeskChat` /
 *       `$openZendeskChat` globals.
 *
 * Tracking: `zendesk_open_chat` emits Rybbit events on every invocation:
 *   - `support:chat:activate`  after the SDK was invoked successfully
 *   - `support:chat:fail`      when the SDK is missing (`sdk_unavailable`)
 *                              or present but exposing no known API
 *                              (`sdk_malformed`)
 *
 * Naming convention: every public function is prefixed with `zendesk_` so the
 * Zendesk origin is obvious at every call site, even when imported alongside
 * other integration modules.
 */

import { rybbit_event } from '@/utilities/integrations/rybbit'

export function zendesk_is_available() {
  return Boolean(window.zE)
}

/**
 * Open the Zendesk chat / messenger widget. Handles both the legacy
 * `zE.activate()` API and the newer messenger `zE('messenger', 'open')` API.
 *
 * Safe to call when the widget is not loaded — silently no-ops on the UI
 * side and emits a `support:chat:fail` Rybbit event for analytics.
 *
 * @param {Object} [params] - Extra properties forwarded to the Rybbit event
 *        (e.g. `cta_id`). Kept generic so call sites can attach the context
 *        that makes sense for the dashboard.
 */
export function zendesk_open_chat(params = {}) {
  if (!zendesk_is_available()) {
    rybbit_event('support:chat:fail', { ...params, error_reason: 'sdk_unavailable' })
    return
  }

  let activated = false
  if (typeof window.zE.activate === 'function') {
    window.zE.activate()
    activated = true
  } else if (typeof window.zE === 'function') {
    window.zE('messenger', 'open')
    activated = true
  }

  if (activated) {
    rybbit_event('support:chat:activate', { ...params })
    return
  }

  // `window.zE` exists but exposes neither the legacy `.activate` method
  // nor the modern callable messenger API — treat as a broken SDK install.
  rybbit_event('support:chat:fail', { ...params, error_reason: 'sdk_malformed' })
}
